import { useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { isNil } from 'ramda';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { schema } from '../utils/schema';

import { DHT_CLA_SETTINGS } from 'RestAPIs/endpoints';
import { useUpdateResource } from 'RestAPIs/useUpdateResource';

import { LoadingButton } from '@mui/lab';
import { Grid } from '@mui/material';

import { ControlledDatePicker, ControlledTextField } from 'components/shared';
import { FormComponentProps } from 'components/shared/CRUD/TableCRUDLayout';
import DrawerFormSkeleton from 'components/shared/CRUD/components/DrawerFormSkeleton';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';

import useGetDHTClaById from '../hooks/useGetDHTClaById';

const EditDHTClaSettingsForm = ({ handleClose, rowId }: FormComponentProps) => {
  const { t } = useTranslation('common');
  const { mutateAsync: edit, isLoading } = useUpdateResource({
    url: DHT_CLA_SETTINGS,
    id: rowId!,
    invalidateQueryKey: ['DHTClaSettings', 'list']
  });
  const { data, isFetching } = useGetDHTClaById(
    !isNil(rowId) ? Number(rowId) : undefined
  );

  const methods = useForm({
    defaultValues: schema.cast(undefined),
    resolver: yupResolver(schema)
  });

  useEffect(() => {
    if (data) {
      methods.reset({
        ...schema.cast(data)
      });
    }
  }, [data]);

  const { control, handleSubmit } = methods;

  const submit = async (formValues) => {
    await edit(formValues).then(handleClose);
  };

  if (isFetching) {
    return <DrawerFormSkeleton count={3} />;
  }

  return (
    <MultiStepForm methods={methods} onSubmit={handleSubmit(submit)}>
      <MultiStepForm.Section
        title={t('DHTClaSettings.formSectionLabels.basis')}
      >
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <ControlledTextField
              label={t('DHTClaSettings.grid.code')}
              control={control}
              variant="outlined"
              type="text"
              disabled
              name="dutyTypeDyn.code"
            />
          </Grid>
          <Grid item xs={12}>
            <ControlledTextField
              label={t('DHTClaSettings.grid.description')}
              control={control}
              variant="outlined"
              type="text"
              disabled
              name="dutyTypeDyn.description"
            />
          </Grid>
          <Grid item xs={6}>
            <ControlledTextField
              label={t('DHTClaSettings.grid.claId')}
              control={control}
              variant="outlined"
              type="text"
              disabled
              name="claCode"
            />
          </Grid>
          <Grid item xs={6}>
            <ControlledTextField
              label={t('DHTClaSettings.grid.dccSvc')}
              control={control}
              variant="outlined"
              type="text"
              disabled
              name="dutyTypeDyn.dccSvc"
            />
          </Grid>
        </Grid>
      </MultiStepForm.Section>
      <MultiStepForm.Section
        title={t('DHTClaSettings.formSectionLabels.codes')}
      >
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <ControlledTextField
              label={t('DHTClaSettings.grid.hcYhsLc')}
              control={control}
              variant="outlined"
              type="text"
              name="hcYhsLc"
            />
          </Grid>
          <Grid item xs={6}>
            <ControlledTextField
              label={t('DHTClaSettings.grid.hcYhsLcSec')}
              control={control}
              variant="outlined"
              type="text"
              name="hcYhsLcSec"
            />
          </Grid>

          <Grid item xs={6}>
            <ControlledTextField
              label={t('DHTClaSettings.grid.hcPayLc')}
              control={control}
              variant="outlined"
              type="text"
              name="hcPayLc"
            />
          </Grid>
          <Grid item xs={6}>
            <ControlledTextField
              label={t('DHTClaSettings.grid.hcPayLcSec')}
              control={control}
              variant="outlined"
              type="text"
              name="hcPayLcSec"
            />
          </Grid>

          <Grid item xs={6}>
            <ControlledTextField
              label={t('DHTClaSettings.grid.zhcYhsLc')}
              control={control}
              variant="outlined"
              type="text"
              name="zhcYhsLc"
            />
          </Grid>
          <Grid item xs={6}>
            <ControlledTextField
              label={t('DHTClaSettings.grid.zhcYhsLcSec')}
              control={control}
              variant="outlined"
              type="text"
              name="zhcYhsLcSec"
            />
          </Grid>

          <Grid item xs={6}>
            <ControlledTextField
              label={t('DHTClaSettings.grid.zhcPayLc')}
              control={control}
              variant="outlined"
              type="text"
              name="zhcPayLc"
            />
          </Grid>
          <Grid item xs={6}>
            <ControlledTextField
              label={t('DHTClaSettings.grid.zhcPayLcSec')}
              control={control}
              variant="outlined"
              type="text"
              name="zhcPayLcSec"
            />
          </Grid>
        </Grid>
      </MultiStepForm.Section>
      <MultiStepForm.Section
        title={t('DHTClaSettings.formSectionLabels.validity')}
      >
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <ControlledDatePicker
              control={control}
              name="dutyTypeDyn.valid.from"
              variant="outlined"
              label={t('validFrom')}
              disabled
            />
          </Grid>
          <Grid item xs={6}>
            <ControlledDatePicker
              control={control}
              name="dutyTypeDyn.valid.to"
              variant="outlined"
              label={t('validTo')}
              disabled
            />
          </Grid>
        </Grid>
      </MultiStepForm.Section>
      <MultiStepForm.Actions>
        <LoadingButton
          type="submit"
          loading={isLoading}
          autoFocus
          color="dark"
          variant="contained"
        >
          {t('save')}
        </LoadingButton>
      </MultiStepForm.Actions>
    </MultiStepForm>
  );
};

export default EditDHTClaSettingsForm;
